import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import AccountCircle from '@mui/icons-material/AccountCircle'
import LoginIcon from '@mui/icons-material/Login'
import { useNavigate } from 'react-router-dom'
import { useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../core/context/AuthContext'
import { TTenantAccount } from '../../core/types/Collaborators'
import UserAccountPermissionController from '../../core/controllers/UserAccountPermissionController'

function UserMenu() {
  const { setUser, setSession } = useContext(AuthContext)
  const navigate = useNavigate()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [accounts, setAccounts] = useState<TTenantAccount[]>([])

  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  )

  const handleLogout = (event: React.MouseEvent<HTMLElement>) => {
    setUser(null)
    navigate('/')
    handleClose()
  }

  const handleSettings = (event: React.MouseEvent<HTMLElement>) => {
    navigate('/settings')
    handleClose()
  }

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleInitialAccounts = useCallback(async () => {
    const res =
      await UserAccountPermissionController.listAccountsByCollaborator()
    if (res.success) {
      setAccounts(res.data ?? [])
    }
  }, [])

  useEffect(() => {
    handleInitialAccounts()
  }, [])

  const handleChangeAccount = async (account: TTenantAccount) => {
    const res = await UserAccountPermissionController.changeTenantAccountUser({
      userAccountPermissionCodeId: account.codeId,
    })
    if (res.success && res.data) {
      setSession({ ...account, token: res.data })
      navigate('/')
      handleClose()
    }
  }

  return (
    <>
      <Tooltip title="Configurações da conta">
        <IconButton
          size="small"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleMenu}
          color="inherit"
          sx={{
            ml: 1,
            p: '0px',
            fontSize: '0px',
            width: isSmallScreen ? '32px' : '48px',
            height: isSmallScreen ? '32px' : '48px',
          }}
        >
          <AccountCircle
            sx={{ fontSize: isSmallScreen ? '28px' : '40px', p: '0px' }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <Avatar />
          </ListItemIcon>
          <ListItemText>Configurações</ListItemText>
        </MenuItem>

        {accounts.length > 0 && (
          <>
            <Divider />
            <ListSubheader>Gerenciar outras contas</ListSubheader>
            {accounts.map((account) => (
              <MenuItem
                key={account.id}
                onClick={() => handleChangeAccount(account)}
              >
                <ListItemIcon>
                  <LoginIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{account.name}</ListItemText>
              </MenuItem>
            ))}
          </>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sair</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

export default UserMenu
